import { Bitmap, ColorMatrix, ColorMatrixFilter, MovieClip } from "../runtime";
import { SPR_blackhole } from "./assets";
import { SWORD_HUES } from "./data/swordColours";
import { SpriteLayer } from "./SpriteLayer";

/**
 * The scythe's black hole: a looping 12-frame 128px sprite that eases up to a
 * sword-dependent scale, pulls enemies in for 190 frames, then fades out. It
 * reports the halfway point of that fade so Game can release its victims
 * before the sprite disappears.
 */
export class BlackHole extends MovieClip {
  alive = false;
  isOn = false;
  xPos = 0;
  yPos = 0;
  private readonly tileSize = 128;
  private readonly rowX = 512;
  private readonly frameLimit = 12;
  private readonly animSpeed = 2;
  private readonly lifeLimit = 190;
  private readonly fadeRate = 5;
  private readonly layer: SpriteLayer;
  private frame = 0;
  private frameCounter = 0;
  private alphaCurrent = 100;
  private fading = false;
  private life = 0;
  private targetScale = 0;

  constructor() {
    super();
    this.layer = new SpriteLayer(
      new SPR_blackhole(0, 0),
      this.tileSize,
      this.tileSize,
      this.rowX,
    );
    const canvasBitmap = new Bitmap(this.layer.canvas);
    this.addChild(canvasBitmap);
    canvasBitmap.x = this.tileSize * -0.5;
    canvasBitmap.y = this.tileSize * -0.5;
    canvasBitmap.smoothing = true;
    this.drawFrame();
    this.visible = false;
  }

  /** 0 = running, 1 = pull released at 50% alpha, 2 = fully faded out. */
  update(offsetX = 0, offsetY = 0): number {
    let returnAction = 0;
    ++this.life;
    if (this.life === this.lifeLimit) {
      this.fading = true;
    }
    if (this.scaleX !== this.targetScale) {
      this.scaleX += (this.targetScale - this.scaleX) * 0.06;
      this.scaleY = this.scaleX;
    }
    ++this.frameCounter;
    if (this.frameCounter >= this.animSpeed) {
      this.frameCounter = 0;
      ++this.frame;
      if (this.frame >= this.frameLimit) {
        this.frame = 0;
      }
      this.drawFrame();
    }
    if (this.fading) {
      this.alphaCurrent -= this.fadeRate;
      this.alpha = this.alphaCurrent * 0.01;
      if (this.alphaCurrent === 50) {
        this.alive = false;
        returnAction = 1;
      }
      if (this.alphaCurrent === 0) {
        this.isOn = false;
        this.visible = false;
        returnAction = 2;
      }
    }
    this.x = this.xPos + offsetX;
    this.y = this.yPos + offsetY;
    return returnAction;
  }

  appear(x = 0, y = 0, swordLevel = 0): void {
    this.xPos = x;
    this.yPos = y;
    this.visible = true;
    this.scaleX = 0.1;
    this.scaleY = 0.1;
    this.isOn = true;
    this.alphaCurrent = 100;
    this.alpha = 1;
    this.fading = false;
    this.alive = true;
    this.frame = 0;
    this.frameCounter = 0;
    this.life = 0;
    this.targetScale = 1.25 + swordLevel * 0.4;
    // level 0 keeps whatever filter a previous appearance left in place
    if (swordLevel > 0) {
      const swordMatrix = new ColorMatrix();
      swordMatrix.adjustColor(0, 0, 0, SWORD_HUES[swordLevel]);
      this.filters = [new ColorMatrixFilter(swordMatrix)];
    }
  }

  private drawFrame(): void {
    this.layer.draw(this.frame);
  }
}
